import { useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Maximize2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import type { ReportGalleryItem } from "./types";

function padTwo(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function clampReportZoom(next: number): number {
  return Math.min(Math.max(0.5, next), 4);
}

interface ReportPhotoViewerProps {
  item: ReportGalleryItem | null;
  photoIndex: number;
  onIndexChange: (next: number) => void;
}

export function ReportPhotoViewer({ item, photoIndex, onIndexChange }: ReportPhotoViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const photos = item?.photos ?? [];
  const current = photos.length > 0
    ? photos[Math.min(photoIndex, photos.length - 1)]
    : null;
  const currentId = current?.id;

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentId]);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) => clampReportZoom(z + (e.deltaY > 0 ? -0.2 : 0.2)));
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  if (!item) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Select an item from the left to view its photos
        </p>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">
          No photos attached to this item
        </p>
      </div>
    );
  }

  const atStart = photoIndex <= 0;
  const atEnd = photoIndex >= photos.length - 1;

  const download = async () => {
    try {
      const res = await fetch(current.url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = current.document_name || "photo.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Photo download failed:", err);
    }
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <span className="text-sm text-muted-foreground tabular-nums">
          {padTwo(photoIndex + 1)} / {padTwo(photos.length)}
        </span>
        <div className="flex items-center gap-1 text-muted-foreground">
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="p-1.5 hover:text-foreground" aria-label="Viewer controls help">
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              Scroll wheel or toolbar to zoom; drag to pan when zoomed in. Full screen uses the browser fullscreen control.
            </TooltipContent>
          </Tooltip>
          <button type="button" onClick={() => setZoom((z) => clampReportZoom(z + 0.2))} className="p-1.5 hover:text-foreground" aria-label="Zoom in">
            <ZoomIn className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setZoom((z) => clampReportZoom(z - 0.2))} className="p-1.5 hover:text-foreground" aria-label="Zoom out">
            <ZoomOut className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="p-1.5 hover:text-foreground" aria-label="Reset zoom">
            <RotateCcw className="h-4 w-4" />
          </button>
          <button type="button" onClick={download} className="p-1.5 hover:text-foreground" aria-label="Download photo">
            <Download className="h-4 w-4" />
          </button>
          <button type="button" onClick={toggleFullscreen} className="p-1.5 hover:text-foreground" aria-label="Fullscreen">
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        ref={viewerRef}
        className="relative flex-1 flex items-center justify-center bg-muted/20 overflow-hidden select-none"
        onMouseDown={(e) => {
          if (zoom <= 1) return;
          setIsPanning(true);
          setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        }}
        onMouseMove={(e) => {
          if (!isPanning) return;
          setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
        }}
        onMouseUp={() => setIsPanning(false)}
        onMouseLeave={() => setIsPanning(false)}
      >
        <img
          src={current.url}
          alt={current.document_name}
          draggable={false}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
            transition: isPanning ? "none" : "transform 0.15s ease-out",
          }}
          className="max-h-full max-w-full object-contain"
        />

        {photos.length > 1 && !atStart && (
          <button
            type="button"
            onClick={() => onIndexChange(photoIndex - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 hover:bg-background flex items-center justify-center"
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {photos.length > 1 && !atEnd && (
          <button
            type="button"
            onClick={() => onIndexChange(photoIndex + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 hover:bg-background flex items-center justify-center"
            aria-label="Next photo"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="border-t border-border px-4 py-3">
        <div className="flex gap-2 overflow-x-auto">
          {photos.map((p, i) => {
            const isActive = i === photoIndex;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onIndexChange(i)}
                className={cn(
                  "relative shrink-0 h-16 w-20 rounded overflow-hidden border",
                  isActive
                    ? "ring-2 ring-primary border-primary"
                    : "border-border hover:border-foreground/40"
                )}
              >
                <img src={p.url} alt={p.document_name} className="h-full w-full object-cover" />
                <span className="absolute top-1 left-1 bg-background/80 text-foreground text-[10px] px-1 rounded tabular-nums">
                  {padTwo(i + 1)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
